import type { SurveyListSelectDto } from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Score } from '@components/shared/score.component';
import { SurveyCTA } from '@components/shared/survey-cta.component';
import { Badge } from '@components/ui/badge';
import { useDeleteCyberAssessment } from '@queries/cyber-assessment.query';
import { useGetCurrentUser } from '@queries/users.query';
import { getCyberAssessmentDraftProgress } from '@utils/cyber-assessment/model.util';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildHomeCyberAssessmentDraftHref } from './home-technology-surveys.util';
import { useHomeTechnologyDraftItems } from './use-home-technology-draft-items.hook';

type HomeTechnologyDraftSurveysProps = {
  draftSurveys: SurveyListSelectDto[];
};

export const HomeTechnologyDraftSurveys: React.FC<HomeTechnologyDraftSurveysProps> = (props) => {
  const { draftSurveys } = props;
  const navigate = useNavigate();
  const { isInnovator } = useGetCurrentUser();
  const { mutateAsync: deleteCyberAssessment } = useDeleteCyberAssessment();
  const { draftItems } = useHomeTechnologyDraftItems({ draftSurveys });

  return (
    <div className="@container">
      <div className="grid grid-cols-1 gap-4 @[36rem]:grid-cols-2">
        {draftItems.map((item) => {
          if (item.kind === 'cyberAssessment') {
            const technologyId = item.technology?.id ?? item.assessment.technologyId;
            const technologyName = item.technology?.name ?? '';
            const progress = getCyberAssessmentDraftProgress(item.assessment).progress;

            return (
              <div
                className="w-full"
                key={item.assessment.id}
              >
                <SurveyCTA
                  draftCard={{
                    onCardClick: () => {
                      navigate(
                        buildHomeCyberAssessmentDraftHref({
                          cyberAssessmentId: item.assessment.id,
                          technologyId,
                        }),
                      );
                    },
                    onDiscardClick: isInnovator
                      ? async () => {
                          await deleteCyberAssessment({
                            cyberAssessmentId: item.assessment.id,
                            technologyId: item.assessment.technologyId,
                          });
                        }
                      : undefined,
                    onResumeClick: () => {
                      navigate(
                        buildHomeCyberAssessmentDraftHref({
                          cyberAssessmentId: item.assessment.id,
                          technologyId,
                        }),
                      );
                    },
                    progress,
                    renderSummary: () => (
                      <div className="min-w-0 flex-1 space-y-2">
                        {technologyId ? (
                          <Link
                            className="block text-base font-medium text-gray-600 transition-colors hover:text-gray-900 hover:underline"
                            to={`/technology/${technologyId}?tab=cyber&cyberId=${item.assessment.id}`}
                          >
                            {technologyName}
                          </Link>
                        ) : (
                          <div className="text-base font-medium text-gray-600">{technologyName}</div>
                        )}

                        <Score
                          score={null}
                          size="medium"
                          subtitle={
                            <span className="text-xs font-light whitespace-nowrap text-[#1F1C1AB2]">Assessment</span>
                          }
                          title={
                            <div className="flex items-center gap-2">
                              <BlueLabel text="Cyber" />
                              <Badge
                                size="xs"
                                variant="primary"
                              >
                                Draft
                              </Badge>
                            </div>
                          }
                        />
                      </div>
                    ),
                    resumeButtonText: 'Resume',
                  }}
                  showCta={false}
                />
              </div>
            );
          }

          const technologyId = item.survey.technology?.id;
          const technologyName = item.survey.technology?.name ?? '';

          return (
            <div
              className="w-full"
              key={item.survey.id}
            >
              <SurveyCTA
                showCta={false}
                survey={item.survey}
                title={
                  technologyId ? (
                    <Link
                      className="hover:text-gray-900 hover:underline transition-colors"
                      to={`/technology/${technologyId}`}
                    >
                      {technologyName}
                    </Link>
                  ) : (
                    technologyName
                  )
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
